import {
  ButtonGroup,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
} from "@chakra-ui/react";
import { graphql } from "gatsby";
import type { CSSProperties, ReactNode, SyntheticEvent } from "react";
import React, { useState } from "react";
import { OutboundLink } from "../../plugins/gatsby-plugin-google-gtag";
import useExperiment from "../hooks/useExperiment";
import { type Job } from "../hooks/useJobsQuery";
import { PossibleGatsbyCountry } from "../live-sites";
import { trackConversionEvent, trackCustomEvent } from "../utils/analytics";
import HostedApply from "./HostedApply";
import { ButtonSecondary } from "./forms/Buttons";

export type JobLinkProps = {
  children: ReactNode;
  className: string;
  job: Job;
  onClick?: (e: SyntheticEvent) => void;
  cpaModalTx: Queries.CpaModalFragment;
  hostedApplyTx: Queries.HostedApplyFragment;
  submit?: () => void;
  gaCategory: "Path" | "SERP";
};

function CpaModal({
  job,
  cpaModalDisclosure,
  onClickHandler,
  cpaModalTx,
  hostedApplyTx,
  submit,
  gaCategory,
}: {
  job: Job;
  cpaModalDisclosure: ReturnType<typeof useDisclosure>;
  onClickHandler: (e: SyntheticEvent) => void;
  cpaModalTx: JobLinkProps["cpaModalTx"];
  hostedApplyTx: JobLinkProps["hostedApplyTx"];
  submit?: () => void;
  gaCategory: JobLinkProps["gaCategory"];
}) {
  const [userWantsToApply, setUserWantsToApply] = useState(false);
  const { title, company, city, summaryOriginal, url } = job;

  if (userWantsToApply) {
    return (
      <HostedApply
        job={job}
        gaCategory={gaCategory}
        cpaModalDisclosure={cpaModalDisclosure}
        submit={submit}
        hostedApplyTx={hostedApplyTx}
      />
    );
  }

  return (
    <Modal
      isOpen={cpaModalDisclosure.isOpen}
      onClose={cpaModalDisclosure.onClose}
    >
      <ModalOverlay />
      <ModalContent className="mx-4 border border-white" aria-details="">
        <ModalHeader className="flex flex-col gap-2 border-b">
          <h2 dangerouslySetInnerHTML={{ __html: title }} className="pr-4" />
          {company && (
            <p className="flex items-start text-sm text-gray-500">
              <svg
                className="mr-1.5 h-5 w-5 flex-shrink-0 text-gray-400"
                x-description="Heroicon name: solid/users"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"></path>
              </svg>
              {company}
            </p>
          )}
          {city && (
            <p className="flex items-start text-sm text-gray-500">
              <svg
                className="mr-1.5 h-5 w-5 flex-shrink-0 text-gray-400"
                x-description="Heroicon name: solid/location-marker"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                  clipRule="evenodd"
                ></path>
              </svg>
              {city}
            </p>
          )}
        </ModalHeader>
        <ModalCloseButton
          onClick={() => {
            trackCustomEvent({
              category: gaCategory,
              action: "[CPA] Job modal closed",
              label: url,
            });
            if (typeof submit === "function") {
              submit();
            }
          }}
        />
        <ModalBody>
          <div
            className="text-md my-4 flex flex-col gap-2 leading-6 text-gray-500 sm:mx-auto sm:max-w-lg"
            dangerouslySetInnerHTML={{
              __html: summaryOriginal,
            }}
          />
        </ModalBody>

        <ModalFooter
          className="sticky bottom-0 flex flex-col gap-4 rounded-b-md bg-primary-600 pt-4"
          style={
            {
              "--tw-gradient-to": "#fff 2rem",
            } as CSSProperties
          }
        >
          <p className="text-white">{cpaModalTx.question}</p>
          <ButtonGroup>
            <ButtonSecondary
              text={cpaModalTx.skip}
              className="flex-1 whitespace-nowrap text-sm capitalize"
              onClick={() => {
                trackCustomEvent({
                  category: gaCategory,
                  action: "[CPA] Job skipped",
                  label: url,
                });
                if (typeof submit === "function") {
                  submit();
                } else {
                  cpaModalDisclosure.onClose();
                }
              }}
            />
            <OutboundLink
              href={url}
              rel="nofollow noreferrer noopener"
              target="_blank"
              onClick={
                job.isHostedApply
                  ? (event: SyntheticEvent) => {
                      event.preventDefault();
                      setUserWantsToApply(true);
                      trackCustomEvent({
                        category: gaCategory,
                        action: "[Hosted Apply] Modal opened",
                        label: job.id,
                      });
                    }
                  : onClickHandler
              }
              className="disabled:bg-white-100/80 relative flex flex-1 items-center justify-center whitespace-nowrap rounded-md border bg-white px-5 py-3 text-sm font-medium capitalize text-primary-600 shadow hover:bg-primary-100"
            >
              {cpaModalTx.apply}
            </OutboundLink>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export function useShouldShowCpaModal({
  job,
  gaCategory,
  GATSBY_COUNTRY,
}: {
  gaCategory: JobLinkProps["gaCategory"];
  job: Job;
  GATSBY_COUNTRY: PossibleGatsbyCountry;
}) {
  const variants = {
    us: [true, false, false, false, false], // 20% for US
    uk: [true, false, false, false], // 25% for UK
  }[GATSBY_COUNTRY] || [true, true, false, false, false]; // 40% for ROW

  const isInIndeedModalBucket = useExperiment(
    `Indeed Job Desc Modal ${job.partner} - ${GATSBY_COUNTRY} - US 20%, UK 25%, ROW 40%`,
    variants
  );
  if (["indeed"].includes(job.partner?.toLowerCase())) {
    if (isInIndeedModalBucket) {
      return true;
    }
  }
  if (job.isHostedApply) {
    return true;
  }
  if (typeof job.cpa === "undefined") {
    return false;
  }
  if (gaCategory === "SERP" && ["uk"].includes(GATSBY_COUNTRY)) {
    return true;
  }
  if (gaCategory === "Path" && ["us"].includes(GATSBY_COUNTRY)) {
    return true;
  }
  return false;
}

export default function JobLink({
  children,
  className,
  job,
  onClick,
  cpaModalTx,
  hostedApplyTx,
  submit,
  gaCategory,
}: JobLinkProps) {
  const { url, rate, rateCurrency, impressionurl, partner } = job;

  const shouldShowCpaModal = useShouldShowCpaModal({
    job,
    gaCategory,
    GATSBY_COUNTRY: process.env.GATSBY_COUNTRY as PossibleGatsbyCountry,
  });
  const cpaModalDisclosure = useDisclosure();

  function onClickHandler(event) {
    if (shouldShowCpaModal) {
      trackCustomEvent({
        category: gaCategory,
        action: "[CPA] Modal job clicked",
        label: url,
      });
    }
    trackConversionEvent({
      value: rate,
      currency: rateCurrency,
    });
    if (typeof onClick === "function") {
      onClick(event);
    }
  }

  return (
    <>
      <OutboundLink
        href={url}
        rel="nofollow noreferrer noopener"
        target="_blank"
        onClick={(event: SyntheticEvent) => {
          if (
            shouldShowCpaModal &&
            !window.location.pathname.startsWith("/path/2003/")
          ) {
            cpaModalDisclosure.onOpen();
            if (partner?.toLowerCase() === "indeed") {
              trackCustomEvent({
                category: gaCategory,
                action: "[Indeed] Modal open",
                label: url,
              });
            } else {
              trackCustomEvent({
                category: gaCategory,
                action: "[CPA] Modal open",
                label: url,
              });
            }
            event.preventDefault();
            return false;
          }
          onClickHandler(event);
        }}
        className={className}
      >
        {children}
      </OutboundLink>
      {impressionurl ? (
        <img
          alt=""
          className="indeed-tmn-pxl"
          data-src={impressionurl}
          width="1"
          height="1"
          style={{ display: "block", marginBottom: "-1px" }}
        />
      ) : null}

      {shouldShowCpaModal ? (
        <CpaModal
          gaCategory={gaCategory}
          job={job}
          cpaModalDisclosure={cpaModalDisclosure}
          onClickHandler={onClickHandler}
          cpaModalTx={cpaModalTx}
          hostedApplyTx={hostedApplyTx}
          submit={submit}
        />
      ) : null}
    </>
  );
}

export const fragments = graphql`
  fragment CpaModal on Translations {
    apply
    skip
    question
  }
`;
